import {
  FUNKTIONSNAMEN,
  type Monatsmengen,
  type PartnerAendern,
  type PartnerDetailUebersicht,
  type PartnerKatalog,
  type Tagesstaende,
  type VerwaltungAuftrag,
  type VerwaltungEdition,
  type VerwaltungPartner,
} from '@nac2s/shared'
import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { Checkbox } from '@/components/base/checkbox/checkbox'
import { Input } from '@/components/base/input/input'
import { Select } from '@/components/base/select/select'
import { Toggle } from '@/components/base/toggle/toggle'
import { Dialogfenster } from '@/teile/Dialogfenster'
import { Knopf } from '@/teile/Knopf'
import { Reiter } from '@/teile/Reiter'
import { Status } from '@/teile/Status'
import { Fehler, Laedt, Leer, Tabelle } from '@/teile/Tabelle'
import { cx } from '@/utils/cx'
import { ApiError } from '../api/client.js'
import {
  abweichungAnlegen,
  abweichungLoeschen,
  katalogSetzen,
  katalogVerteilen,
  kopplungscodeErzeugen,
  ladeAuftraege,
  ladeEditionen,
  ladeEinenPartner,
  ladeKatalog,
  ladeMengen,
  ladePartnerUebersicht,
  ladeTagesstaende,
  mengenAbrufen,
  partnerAendern,
  serverAbfragen,
  serverAbmelden,
  serverKoppeln,
} from '../api/verwaltung.js'
import {
  aktuellerMonat,
  datum,
  euro,
  istMonat,
  monatsauswahl,
  monatsname,
  zeitpunkt,
} from '../teile/monat.js'
import { Zustandsmarke as Auftragsmarke } from './Auftraege.js'
import { kuerzel } from './Partner.js'

/**
 * Ein Partner in der Verwaltung — dieselbe Bauform wie die Kundendetails der
 * Kundenverwaltung: Kopf, Kennzahlen, vier Register.
 *
 * Mengen: was der Stack täglich meldet, je Kunde, mit dem Peak des Monats.
 * Aufträge: was in seinem Stack auf Freigabe wartet. Preise: die Listen, aus
 * denen die Abrechnung rechnet. Einstellungen: Stammdaten, Stack-Zugang,
 * Server-Kopplung mit dem Code, der nur einmal zu sehen ist.
 */

type Register = 'mengen' | 'auftraege' | 'katalog' | 'einstellungen'
const REGISTER: Register[] = ['mengen', 'auftraege', 'katalog', 'einstellungen']

type Ladezustand =
  | { status: 'laedt' }
  | { status: 'da'; partner: VerwaltungPartner }
  | { status: 'fehler'; meldung: string }

export function PartnerDetail() {
  const { id = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const registerParam = params.get('reiter')
  const register: Register =
    registerParam && (REGISTER as string[]).includes(registerParam)
      ? (registerParam as Register)
      : 'mengen'
  const [zustand, setZustand] = useState<Ladezustand>({ status: 'laedt' })

  const neuLaden = useCallback(async () => {
    try {
      setZustand({ status: 'da', partner: await ladeEinenPartner(id) })
    } catch (fehler: unknown) {
      setZustand({
        status: 'fehler',
        meldung:
          fehler instanceof ApiError && fehler.status === 404
            ? 'Diesen Partner gibt es nicht.'
            : fehler instanceof ApiError
              ? fehler.message
              : 'Unbekannter Fehler',
      })
    }
  }, [id])

  useEffect(() => {
    void neuLaden()
  }, [neuLaden])

  function wechseln(r: Register) {
    const neu = new URLSearchParams(params)
    neu.set('reiter', r)
    setParams(neu)
  }

  if (zustand.status === 'laedt') {
    return (
      <div className="px-6 py-8">
        <Laedt>Partner wird geladen …</Laedt>
      </div>
    )
  }
  if (zustand.status === 'fehler') {
    return (
      <div className="px-6 py-8">
        <Zurueck />
        <div className="mt-4">
          <Fehler>{zustand.meldung}</Fehler>
        </div>
      </div>
    )
  }

  const { partner } = zustand
  const angebunden = Boolean(
    partner.stackEndpoint && partner.server && !partner.server.abgemeldetAm,
  )

  return (
    <div>
      <Zurueck />

      <div className="flex flex-wrap items-start gap-3.5 px-6 pt-2.5">
        <span
          aria-hidden="true"
          className={cx(
            'grid size-11 place-items-center rounded-[14px] font-display text-base font-bold tracking-tight',
            partner.zustand !== 'aktiv'
              ? 'bg-secondary text-tertiary'
              : angebunden
                ? 'bg-success-primary text-fg-success-primary'
                : 'bg-achtung-flaeche text-achtung-tief',
          )}
        >
          {kuerzel(partner.name)}
        </span>
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-[26px] leading-[1.1] font-extrabold tracking-[-0.025em] text-primary">
              {partner.name}
            </h1>
            <Partnermarke p={partner} />
            {partner.autoApprove ? <Status ton="ruhig">Aufträge automatisch</Status> : null}
          </div>
          <p className="mt-0.5 text-[13px] text-quaternary">
            {partner.billingRef} · {partner.kundenDomain ?? 'keine Domain'} · angelegt{' '}
            {datum(partner.angelegtAm)}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Knopf onClick={() => wechseln('einstellungen')}>Einstellungen</Knopf>
        </div>
      </div>

      <Uebersicht partner={partner} angebunden={angebunden} />
      {partner.letzterAbruf?.status === 'fehler' ? (
        <div className="px-6 pt-3">
          <Fehler>
            Letzter Abruf fehlgeschlagen: {partner.letzterAbruf.fehler ?? 'ohne Angabe'}
          </Fehler>
        </div>
      ) : null}

      <div className="mt-[18px] flex gap-5 border-b border-secondary px-6" role="tablist">
        <Reiter an={register === 'mengen'} onWahl={() => wechseln('mengen')}>
          Mengen
        </Reiter>
        <Reiter an={register === 'auftraege'} onWahl={() => wechseln('auftraege')}>
          Aufträge
        </Reiter>
        <Reiter an={register === 'katalog'} onWahl={() => wechseln('katalog')}>
          Katalog
        </Reiter>
        <Reiter an={register === 'einstellungen'} onWahl={() => wechseln('einstellungen')}>
          Einstellungen
        </Reiter>
      </div>

      <div className="px-6 pt-[18px] pb-7">
        {register === 'mengen' ? (
          <Mengen partner={partner} angebunden={angebunden} onAbgerufen={neuLaden} />
        ) : register === 'auftraege' ? (
          <PartnerAuftraege partner={partner} />
        ) : register === 'katalog' ? (
          <Katalog partner={partner} />
        ) : (
          <Einstellungen partner={partner} onGeaendert={neuLaden} />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Kopfzahlen (ADR-0015 Nachtrag): Seeds je Edition heute, Peak je Edition im
// laufenden Monat, Umsatz zweier Monate, abweichende Preise nur, wenn es welche gibt
// ---------------------------------------------------------------------------

type UebersichtZustand =
  | { status: 'laedt' }
  | { status: 'da'; daten: PartnerDetailUebersicht }
  | { status: 'fehler'; meldung: string }

function Uebersicht({ partner, angebunden }: { partner: VerwaltungPartner; angebunden: boolean }) {
  const [zustand, setZustand] = useState<UebersichtZustand>({ status: 'laedt' })
  const [editionsnamen, setEditionsnamen] = useState<ReadonlyMap<string, string>>(new Map())

  useEffect(() => {
    let aktuell = true
    Promise.all([ladePartnerUebersicht(partner.id), ladeEditionen()])
      .then(([daten, editionen]) => {
        if (!aktuell) return
        setZustand({ status: 'da', daten })
        setEditionsnamen(new Map(editionen.map((e) => [e.key, e.name])))
      })
      .catch((fehler: unknown) => {
        if (aktuell)
          setZustand({
            status: 'fehler',
            meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
          })
      })
    return () => {
      aktuell = false
    }
  }, [partner])

  const name = (key: string) => editionsnamen.get(key) ?? key
  const daten = zustand.status === 'da' ? zustand.daten : null
  const seedsGesamt = daten ? Object.values(daten.seedsJeEdition).reduce((s, n) => s + n, 0) : 0

  return (
    <div className="flex flex-col gap-3 px-6 pt-[18px]">
      <dl className="flex flex-wrap gap-[30px]">
        <Wert name="Seeds heute">
          {daten ? (
            <>
              {seedsGesamt}
              {Object.keys(daten.seedsJeEdition).length > 0 ? (
                <small className="ml-2 text-sm font-normal text-tertiary">
                  {Object.entries(daten.seedsJeEdition)
                    .map(([k, n]) => `${name(k)} ${n}`)
                    .join(' · ')}
                </small>
              ) : null}
            </>
          ) : (
            '—'
          )}
        </Wert>
        <Wert name={daten ? `Peak ${monatsname(daten.laufenderMonat)}` : 'Peak'}>
          {daten && Object.keys(daten.peakJeEdition).length > 0 ? (
            <span className="text-base font-semibold">
              {Object.entries(daten.peakJeEdition)
                .map(([k, n]) => `${name(k)} ${n}`)
                .join(' · ')}
            </span>
          ) : (
            '—'
          )}
        </Wert>
        <Wert name={daten ? `Umsatz ${monatsname(daten.laufenderMonat)}` : 'Umsatz laufend'}>
          {daten ? euro(daten.umsatzLaufendCent) : '—'}
        </Wert>
        <Wert name={daten ? `Umsatz ${monatsname(daten.letzterMonat)}` : 'Umsatz letzter'}>
          {daten ? euro(daten.umsatzLetzterCent) : '—'}
        </Wert>
        <Wert name="Stack" achtung={!angebunden}>
          <span className="text-base font-normal">
            {!angebunden
              ? 'nicht angebunden'
              : partner.letzterAbruf?.status === 'fehler'
                ? 'gestört'
                : partner.letzterAbruf
                  ? `antwortet · Abruf ${zeitpunkt(partner.letzterAbruf.am)}`
                  : 'noch kein Abruf'}
          </span>
        </Wert>
      </dl>
      {daten && daten.abweichendePreise.length > 0 ? (
        <p className="text-[12.5px] text-tertiary">
          <span className="font-semibold text-akzent-tief">Vereinbarte Preise:</span>{' '}
          {daten.abweichendePreise
            .map(
              (w) =>
                `${name(w.edition)} ${w.seedCent === null ? '—' : euro(w.seedCent)} je Seed${
                  w.quelle === 'rabatt'
                    ? ` (${w.abweichung?.wert} % Rabatt auf ${w.vkCent === null ? '—' : euro(w.vkCent)})`
                    : ' (fester Preis)'
                }`,
            )
            .join(' · ')}
        </p>
      ) : null}
      {zustand.status === 'fehler' ? <Fehler>{zustand.meldung}</Fehler> : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mengen
// ---------------------------------------------------------------------------

type MengenZustand =
  | { status: 'laedt' }
  | { status: 'da'; monat: Monatsmengen; tage: Tagesstaende }
  | { status: 'fehler'; meldung: string }

function Mengen({
  partner,
  angebunden,
  onAbgerufen,
}: {
  partner: VerwaltungPartner
  angebunden: boolean
  onAbgerufen: () => Promise<void>
}) {
  const [params, setParams] = useSearchParams()
  const monatParam = params.get('monat')
  const monat = monatParam && istMonat(monatParam) ? monatParam : aktuellerMonat()
  const [zustand, setZustand] = useState<MengenZustand>({ status: 'laedt' })
  const [abruf, setAbruf] = useState<{ laeuft: boolean; meldung: string | null }>({
    laeuft: false,
    meldung: null,
  })

  const laden = useCallback(async () => {
    try {
      const [m, t] = await Promise.all([
        ladeMengen(partner.id, monat),
        ladeTagesstaende(partner.id, monat),
      ])
      setZustand({ status: 'da', monat: m, tage: t })
    } catch (fehler: unknown) {
      setZustand({
        status: 'fehler',
        meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
      })
    }
  }, [partner.id, monat])

  useEffect(() => {
    void laden()
  }, [laden])

  async function jetztAbrufen() {
    setAbruf({ laeuft: true, meldung: null })
    try {
      const ergebnis = await mengenAbrufen(partner.id)
      setAbruf({
        laeuft: false,
        meldung:
          ergebnis.status === 'ok'
            ? null
            : `Der Abruf ist fehlgeschlagen: ${ergebnis.fehler ?? 'ohne Angabe'}`,
      })
      await Promise.all([laden(), onAbgerufen()])
    } catch (fehler: unknown) {
      setAbruf({
        laeuft: false,
        meldung: fehler instanceof ApiError ? fehler.message : 'Der Abruf kam nicht an.',
      })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end gap-3">
        <Monatswahl
          monat={monat}
          onWahl={(m) => {
            const neu = new URLSearchParams(params)
            neu.set('monat', m)
            setParams(neu)
          }}
        />
        <div className="ml-auto">
          <Knopf stark disabled={!angebunden || abruf.laeuft} onClick={() => void jetztAbrufen()}>
            {abruf.laeuft ? 'Abruf läuft …' : 'Jetzt abrufen'}
          </Knopf>
        </div>
      </div>
      {!angebunden ? (
        <p className="text-sm text-tertiary">
          Der Stack ist nicht angebunden — Adresse und Server-Kopplung stehen in den Einstellungen.
          Bis dahin gibt es keine Mengen.
        </p>
      ) : null}
      {abruf.meldung ? <Fehler>{abruf.meldung}</Fehler> : null}

      {zustand.status === 'laedt' ? (
        <Laedt>Mengen werden geladen …</Laedt>
      ) : zustand.status === 'fehler' ? (
        <Fehler>{zustand.meldung}</Fehler>
      ) : zustand.monat.abrufTage === 0 ? (
        <Leer titel={`Kein Abruf im ${monatsname(monat)}`}>
          Die Verwaltung holt die Mengen jede Nacht. Ein Abruf von Hand geht oben rechts.
        </Leer>
      ) : (
        <>
          <dl className="flex flex-wrap gap-[30px]">
            <Wert name="Abruftage">{zustand.monat.abrufTage}</Wert>
            <Wert name="Kunden (Peak)">{zustand.monat.kundenPeak}</Wert>
            {Object.entries(zustand.monat.seedsPeak).map(([edition, peak]) => (
              <Wert key={edition} name={`Seeds ${edition} (Peak)`}>
                {peak}
              </Wert>
            ))}
          </dl>

          <section>
            <h2 className="mb-2 text-[13px] font-semibold text-primary">Je Kunde</h2>
            <Tabelle
              kopf={['Kunde', 'Edition', 'Seeds (Peak)', 'Berechnet', 'Lizenz', 'Tage aktiv', '']}
            >
              {zustand.monat.jeKunde.map((k) => (
                <tr key={k.ref} className="border-b border-secondary last:border-b-0">
                  <td className="py-2 pr-2.5">
                    <span className="block text-primary">{k.name}</span>
                    <span className="block text-[11.5px] text-quaternary">{k.ref}</span>
                  </td>
                  <td className="py-2 pr-2.5 text-tertiary">{k.edition}</td>
                  <td className="py-2 pr-2.5 text-tertiary tabular-nums">{k.seedsPeak}</td>
                  <td className="py-2 pr-2.5 text-primary tabular-nums">{k.menge}</td>
                  <td className="py-2 pr-2.5 text-[12.5px] text-tertiary">
                    {k.lizenz ? (
                      <>
                        <span className="block">
                          {k.lizenz.laufzeit === 'monatlich'
                            ? 'monatlich'
                            : `Mindestlaufzeit ${k.lizenz.mindestlaufzeitMonate} Monate`}
                          {` · mind. ${k.lizenz.mindestmenge}`}
                        </span>
                        <span className="block text-[11.5px] text-quaternary tabular-nums">
                          seit {datum(k.lizenz.start)} · bis {datum(k.lizenz.laufzeitEnde)}
                        </span>
                      </>
                    ) : (
                      <span className="text-quaternary">keine</span>
                    )}
                  </td>
                  <td className="py-2 pr-2.5 text-tertiary tabular-nums">{k.tageAktiv}</td>
                  <td className="py-2 text-right">
                    {k.istDemo ? <Status ton="ruhig">Demo, zählt nicht</Status> : null}
                  </td>
                </tr>
              ))}
            </Tabelle>
          </section>

          <section>
            <h2 className="mb-2 text-[13px] font-semibold text-primary">
              Tagesstände{' '}
              <span className="font-normal text-tertiary">— so setzt sich der Monat zusammen</span>
            </h2>
            <Tabelle kopf={['Tag', 'Kunden', 'Seeds', 'Zusammensetzung']}>
              {zustand.tage.tage.map((t) => {
                const zaehlbar = t.kunden.filter((k) => !k.istDemo && k.zustand === 'aktiv')
                return (
                  <tr key={t.tag} className="border-b border-secondary last:border-b-0">
                    <td className="py-2 pr-2.5 text-primary tabular-nums">{datum(t.tag)}</td>
                    <td className="py-2 pr-2.5 text-tertiary tabular-nums">{zaehlbar.length}</td>
                    <td className="py-2 pr-2.5 text-tertiary tabular-nums">
                      {zaehlbar.reduce((s, k) => s + (k.seeds ?? 0), 0)}
                    </td>
                    <td className="py-2 text-[12.5px] text-tertiary">
                      {t.kunden
                        .map(
                          (k) =>
                            `${k.name}: ${k.seeds ?? '?'}${k.istDemo ? ' (Demo)' : ''}${
                              k.zustand !== 'aktiv' ? ` (${k.zustand})` : ''
                            }`,
                        )
                        .join('; ')}
                    </td>
                  </tr>
                )
              })}
            </Tabelle>
          </section>
        </>
      )}
    </div>
  )
}

function Monatswahl({ monat, onWahl }: { monat: string; onWahl: (m: string) => void }) {
  return (
    <Select
      size="sm"
      label="Monat"
      items={monatsauswahl()}
      selectedKey={monat}
      onSelectionChange={(schluessel) => {
        if (typeof schluessel === 'string') onWahl(schluessel)
      }}
      className="w-[12rem]"
    >
      {(eintrag) => <Select.Item {...eintrag} />}
    </Select>
  )
}

// ---------------------------------------------------------------------------
// Aufträge dieses Partners
// ---------------------------------------------------------------------------

type AuftraegeZustand =
  | { status: 'laedt' }
  | { status: 'da'; auftraege: VerwaltungAuftrag[]; erreichbar: boolean }
  | { status: 'fehler'; meldung: string }

function PartnerAuftraege({ partner }: { partner: VerwaltungPartner }) {
  const [zustand, setZustand] = useState<AuftraegeZustand>({ status: 'laedt' })

  useEffect(() => {
    let aktuell = true
    ladeAuftraege()
      .then((antwort) => {
        if (!aktuell) return
        setZustand({
          status: 'da',
          auftraege: antwort.auftraege.filter((a) => a.partnerId === partner.id),
          erreichbar: !antwort.nichtErreichbar.some((p) => p.partnerId === partner.id),
        })
      })
      .catch((fehler: unknown) => {
        if (aktuell)
          setZustand({
            status: 'fehler',
            meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
          })
      })
    return () => {
      aktuell = false
    }
  }, [partner.id])

  if (zustand.status === 'laedt') return <Laedt>Aufträge werden beim Stack abgefragt …</Laedt>
  if (zustand.status === 'fehler') return <Fehler>{zustand.meldung}</Fehler>
  if (!zustand.erreichbar) {
    return (
      <Fehler>Der Stack hat nicht geantwortet — die Aufträge sind gerade nicht sichtbar.</Fehler>
    )
  }
  if (zustand.auftraege.length === 0) {
    return (
      <Leer titel="Keine Aufträge">
        Freigeben und Zurückziehen geht über{' '}
        <Link to="/auftraege" className="text-primary underline">
          Aufträge
        </Link>
        , sobald etwas ansteht.
      </Leer>
    )
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-tertiary">
        Entscheiden lässt sich unter{' '}
        <Link to="/auftraege" className="text-primary underline">
          Aufträge
        </Link>
        .
      </p>
      <Tabelle kopf={['Kunde', 'Art', 'Beantragt', 'Von', 'Zustand']}>
        {zustand.auftraege.map((a) => (
          <tr key={a.id} className="border-b border-secondary last:border-b-0">
            <td className="py-2.5 pr-2.5">
              <span className="block text-primary">{a.anzeigename}</span>
              <span className="block text-[11.5px] text-quaternary">{a.host}</span>
            </td>
            <td className="py-2.5 pr-2.5 text-tertiary">{a.art}</td>
            <td className="py-2.5 pr-2.5 text-tertiary tabular-nums">{zeitpunkt(a.beantragtAm)}</td>
            <td className="py-2.5 pr-2.5 text-tertiary">{a.beantragtVon ?? '—'}</td>
            <td className="py-2.5 text-right">
              <Auftragsmarke a={a} />
            </td>
          </tr>
        ))}
      </Tabelle>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Katalog — was dieser Partner verkaufen darf, zu welchem Preis, mit welcher
// Mindestlaufzeit (ADR-0014, ADR-0015)
// ---------------------------------------------------------------------------

/** „12,50" oder „12.50" → 1250; alles andere → null. */
export function cent(eingabe: string): number | null {
  const t = eingabe.trim().replace(/\s|€/g, '').replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null
  return Math.round(Number(t) * 100)
}

type KatalogZustand =
  | { status: 'laedt' }
  | { status: 'da'; editionen: VerwaltungEdition[]; katalog: PartnerKatalog }
  | { status: 'fehler'; meldung: string }

function Katalog({ partner }: { partner: VerwaltungPartner }) {
  const [zustand, setZustand] = useState<KatalogZustand>({ status: 'laedt' })
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set())
  const [laufzeit, setLaufzeit] = useState<Record<string, string>>({})
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const [abweichungOffen, setAbweichungOffen] = useState(false)

  const laden = useCallback(async () => {
    try {
      const [editionen, katalog] = await Promise.all([ladeEditionen(), ladeKatalog(partner.id)])
      setZustand({ status: 'da', editionen, katalog })
      setGewaehlt(new Set(katalog.editionen))
      setLaufzeit(
        Object.fromEntries(Object.entries(katalog.mindestlaufzeit).map(([k, v]) => [k, String(v)])),
      )
    } catch (fehler: unknown) {
      setZustand({
        status: 'fehler',
        meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
      })
    }
  }, [partner.id])

  useEffect(() => {
    void laden()
  }, [laden])

  if (zustand.status === 'laedt') return <Laedt>Katalog wird geladen …</Laedt>
  if (zustand.status === 'fehler') return <Fehler>{zustand.meldung}</Fehler>

  const { editionen, katalog } = zustand
  const laufzeitZahlen: Record<string, number> = {}
  let laufzeitGueltig = true
  for (const [k, v] of Object.entries(laufzeit)) {
    if (v.trim() === '') continue
    const n = Number(v)
    if (!Number.isInteger(n) || n < 1 || n > 120) laufzeitGueltig = false
    else laufzeitZahlen[k] = n
  }
  const geaendert =
    gewaehlt.size !== katalog.editionen.length ||
    katalog.editionen.some((k) => !gewaehlt.has(k)) ||
    JSON.stringify(laufzeitZahlen) !== JSON.stringify(katalog.mindestlaufzeit)

  async function speichern() {
    setLaeuft(true)
    setMeldung(null)
    try {
      const nurGewaehlt = Object.fromEntries(
        Object.entries(laufzeitZahlen).filter(([k]) => gewaehlt.has(k)),
      )
      const ergebnis = await katalogSetzen(partner.id, [...gewaehlt], nurGewaehlt)
      setMeldung(
        ergebnis.verteilung.status === 'ok'
          ? { art: 'ok', text: 'Gespeichert und an den Stack verteilt.' }
          : {
              art: 'fehler',
              text: `Gespeichert, aber nicht verteilt: ${ergebnis.verteilung.fehler ?? 'ohne Angabe'}`,
            },
      )
      await laden()
    } catch (fehler: unknown) {
      setMeldung({
        art: 'fehler',
        text: fehler instanceof ApiError ? fehler.message : 'Nicht gespeichert.',
      })
    } finally {
      setLaeuft(false)
    }
  }

  async function verteilen() {
    setLaeuft(true)
    setMeldung(null)
    try {
      const ergebnis = await katalogVerteilen(partner.id)
      setMeldung(
        ergebnis.status === 'ok'
          ? { art: 'ok', text: 'An den Stack verteilt.' }
          : { art: 'fehler', text: `Nicht verteilt: ${ergebnis.fehler ?? 'ohne Angabe'}` },
      )
      await laden()
    } finally {
      setLaeuft(false)
    }
  }

  async function abweichungWeg(id: string) {
    setLaeuft(true)
    setMeldung(null)
    try {
      await abweichungLoeschen(partner.id, id)
      await laden()
    } catch (fehler: unknown) {
      setMeldung({
        art: 'fehler',
        text: fehler instanceof ApiError ? fehler.message : 'Nicht gelöscht.',
      })
    } finally {
      setLaeuft(false)
    }
  }

  const preisVon = (key: string) => katalog.preise.find((p) => p.edition === key) ?? null

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start gap-3">
        <p className="max-w-[40rem] text-sm text-tertiary">
          Nur was hier steht, kann der Partner einem Kunden verkaufen. Der Katalog geht mit den
          wirksamen Preisen und Laufzeiten an seinen Stack — nach jeder Änderung und einmal täglich.
        </p>
        <div className="ml-auto flex gap-2">
          <Knopf disabled={laeuft} onClick={() => void verteilen()}>
            An Stack verteilen
          </Knopf>
          <Knopf
            stark
            disabled={laeuft || !geaendert || !laufzeitGueltig}
            onClick={() => void speichern()}
          >
            Speichern
          </Knopf>
        </div>
      </div>
      <p className="text-[12.5px] text-quaternary">
        {katalog.verteiltAm
          ? `Zuletzt verteilt ${zeitpunkt(katalog.verteiltAm)}`
          : 'Noch nie verteilt'}
        {katalog.verteilungFehler ? ` · letzter Fehler: ${katalog.verteilungFehler}` : ''}
      </p>

      {editionen.length === 0 ? (
        <Leer titel="Keine Editionen">Erst unter „Editionen" eine anlegen.</Leer>
      ) : (
        <Tabelle kopf={['Im Katalog', 'Preis je Seed und Monat', 'Mindestlaufzeit', '']}>
          {editionen.map((e) => {
            const preis = preisVon(e.key)
            const an = gewaehlt.has(e.key)
            return (
              <tr key={e.key} className="border-b border-secondary last:border-b-0">
                <td className="py-2.5 pr-2.5">
                  <Checkbox
                    size="md"
                    label={e.name}
                    hint={[
                      e.funktionen.map((f) => FUNKTIONSNAMEN[f].name).join(', ') ||
                        'keine Funktion',
                      e.eolAm ? `endet ${datum(e.eolAm)}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    isSelected={an}
                    onChange={(neu) =>
                      setGewaehlt((alt) => {
                        const kopie = new Set(alt)
                        if (neu) kopie.add(e.key)
                        else kopie.delete(e.key)
                        return kopie
                      })
                    }
                  />
                </td>
                <td className="py-2.5 pr-2.5 tabular-nums">
                  {!an ? (
                    <span className="text-quaternary">—</span>
                  ) : preis?.seedCent === null || preis === null ? (
                    <Status ton="wartet">kein Preis</Status>
                  ) : (
                    <span className="flex items-center gap-2">
                      <span className="text-primary">{euro(preis.seedCent)}</span>
                      {preis.quelle !== 'vk' ? <Status ton="wartet">vereinbart</Status> : null}
                    </span>
                  )}
                </td>
                <td className="py-2.5 pr-2.5">
                  <Input
                    size="sm"
                    aria-label={`Mindestlaufzeit ${e.name}`}
                    inputMode="numeric"
                    value={laufzeit[e.key] ?? ''}
                    onChange={(w) => setLaufzeit((alt) => ({ ...alt, [e.key]: w }))}
                    placeholder={`${e.mindestlaufzeitMonate} (Vorgabe)`}
                    isDisabled={!an}
                    className="w-[9rem]"
                  />
                </td>
                <td className="py-2.5 text-[11.5px] text-quaternary">
                  Monate · jährlich −{e.monatlichAufschlagProzent} %
                </td>
              </tr>
            )
          })}
        </Tabelle>
      )}

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start gap-3">
          <div>
            <h2 className="text-[13px] font-semibold text-primary">Preisabweichungen</h2>
            <p className="text-sm text-tertiary">
              Fester Preis oder Rabatt, nur für diesen Partner, ab Monat und wahlweise bis Monat.
              Ein fester Preis geht vor.
            </p>
          </div>
          <div className="ml-auto">
            <Knopf onClick={() => setAbweichungOffen(true)} disabled={editionen.length === 0}>
              Abweichung anlegen
            </Knopf>
          </div>
        </div>
        {katalog.abweichungen.length === 0 ? (
          <p className="text-[12.5px] text-quaternary">Keine — es gilt der VK.</p>
        ) : (
          <Tabelle kopf={['Edition', 'Art', 'Wert', 'Gültig ab', 'Bis', '']}>
            {katalog.abweichungen.map((a) => (
              <tr key={a.id} className="border-b border-secondary last:border-b-0">
                <td className="py-2 pr-2.5 text-primary">
                  {editionen.find((e) => e.key === a.edition)?.name ?? a.edition}
                </td>
                <td className="py-2 pr-2.5 text-tertiary">
                  {a.art === 'preis' ? 'fester Preis' : 'Rabatt'}
                </td>
                <td className="py-2 pr-2.5 text-primary tabular-nums">
                  {a.art === 'preis' ? euro(a.wert) : `${a.wert} %`}
                </td>
                <td className="py-2 pr-2.5 text-tertiary tabular-nums">
                  {monatsname(a.gueltigAb)}
                </td>
                <td className="py-2 pr-2.5 text-tertiary tabular-nums">
                  {a.gueltigBis ? monatsname(a.gueltigBis) : 'offen'}
                </td>
                <td className="py-2 text-right">
                  <Knopf gefahr disabled={laeuft} onClick={() => void abweichungWeg(a.id)}>
                    Löschen
                  </Knopf>
                </td>
              </tr>
            ))}
          </Tabelle>
        )}
      </section>

      {meldung ? (
        meldung.art === 'ok' ? (
          <p role="status" className="text-sm text-tertiary">
            {meldung.text}
          </p>
        ) : (
          <Fehler>{meldung.text}</Fehler>
        )
      ) : null}

      {abweichungOffen ? (
        <AbweichungFormular
          partnerId={partner.id}
          editionen={editionen.filter((e) => gewaehlt.has(e.key))}
          onAbbruch={() => setAbweichungOffen(false)}
          onAngelegt={() => {
            setAbweichungOffen(false)
            void laden()
          }}
        />
      ) : null}
    </div>
  )
}

function AbweichungFormular({
  partnerId,
  editionen,
  onAbbruch,
  onAngelegt,
}: {
  partnerId: string
  editionen: VerwaltungEdition[]
  onAbbruch: () => void
  onAngelegt: () => void
}) {
  const [edition, setEdition] = useState(editionen[0]?.key ?? '')
  const [art, setArt] = useState<'preis' | 'rabatt'>('rabatt')
  const [wert, setWert] = useState('')
  const [gueltigAb, setGueltigAb] = useState(aktuellerMonat())
  const [gueltigBis, setGueltigBis] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  const wertZahl =
    art === 'preis' ? cent(wert) : /^\d{1,3}$/.test(wert.trim()) ? Number(wert) : null
  const gueltig =
    edition !== '' &&
    wertZahl !== null &&
    (art !== 'rabatt' || wertZahl <= 100) &&
    istMonat(gueltigAb) &&
    (gueltigBis === '' || (istMonat(gueltigBis) && gueltigBis >= gueltigAb))

  async function absenden(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    if (wertZahl === null) return
    setLaeuft(true)
    setMeldung(null)
    try {
      await abweichungAnlegen(partnerId, {
        edition,
        art,
        wert: wertZahl,
        gueltigAb,
        gueltigBis: gueltigBis === '' ? null : gueltigBis,
      })
      onAngelegt()
    } catch (fehler: unknown) {
      setMeldung(fehler instanceof ApiError ? fehler.message : 'Nicht angelegt.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Dialogfenster
      titel="Preisabweichung anlegen"
      satz="Gilt nur für diesen Partner. Ein fester Preis ersetzt den VK, ein Rabatt zieht Prozent davon ab."
      breite="w-[min(30rem,calc(100vw-2rem))]"
      onSchliessen={onAbbruch}
    >
      <form onSubmit={absenden} className="flex flex-col gap-4 pb-6">
        <Select
          size="sm"
          label="Edition"
          items={editionen.map((e) => ({ id: e.key, label: e.name }))}
          selectedKey={edition}
          onSelectionChange={(k) => setEdition(String(k))}
        >
          {(eintrag) => <Select.Item {...eintrag} />}
        </Select>
        <Select
          size="sm"
          label="Art"
          items={[
            { id: 'rabatt', label: 'Rabatt in Prozent' },
            { id: 'preis', label: 'Fester Preis je Seed und Monat' },
          ]}
          selectedKey={art}
          onSelectionChange={(k) => setArt(k === 'preis' ? 'preis' : 'rabatt')}
        >
          {(eintrag) => <Select.Item {...eintrag} />}
        </Select>
        <Input
          isRequired
          label={art === 'preis' ? 'Preis in Euro' : 'Rabatt in Prozent'}
          size="md"
          inputMode="decimal"
          value={wert}
          onChange={setWert}
          placeholder={art === 'preis' ? '0,00' : '10'}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            isRequired
            label="Gültig ab"
            hint="Monat als JJJJ-MM."
            size="md"
            value={gueltigAb}
            onChange={setGueltigAb}
          />
          <Input
            label="Gültig bis"
            hint="Leer = offen."
            size="md"
            value={gueltigBis}
            onChange={setGueltigBis}
            placeholder="JJJJ-MM"
          />
        </div>
        {meldung ? <Fehler>{meldung}</Fehler> : null}
        <div className="flex justify-end gap-2">
          <Knopf onClick={onAbbruch} disabled={laeuft}>
            Abbrechen
          </Knopf>
          <Knopf type="submit" stark disabled={laeuft || !gueltig}>
            Anlegen
          </Knopf>
        </div>
      </form>
    </Dialogfenster>
  )
}

// ---------------------------------------------------------------------------
// Einstellungen
// ---------------------------------------------------------------------------

function Einstellungen({
  partner,
  onGeaendert,
}: {
  partner: VerwaltungPartner
  onGeaendert: () => Promise<void>
}) {
  const [name, setName] = useState(partner.name)
  const [billingRef, setBillingRef] = useState(partner.billingRef)
  const [subdomain, setSubdomain] = useState(partner.subdomain ?? '')
  const [stackHost, setStackHost] = useState(partner.stackHost ?? '')
  const [stackEndpoint, setStackEndpoint] = useState(partner.stackEndpoint ?? '')
  const [baseDomain, setBaseDomain] = useState(partner.baseDomain ?? '')
  const [whiteLabel, setWhiteLabel] = useState(partner.whiteLabel)
  const [autoApprove, setAutoApprove] = useState(partner.autoApprove)
  const [notizen, setNotizen] = useState(partner.notizen ?? '')
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const [zustandLaeuft, setZustandLaeuft] = useState(false)
  const [freigegebeneVersion, setFreigegebeneVersion] = useState(partner.freigegebeneVersion ?? '')
  const [kopplungscode, setKopplungscode] = useState<{ code: string; gueltigBis: string } | null>(
    null,
  )
  const [serverLaeuft, setServerLaeuft] = useState(false)

  const aenderung: PartnerAendern = {}
  if (name.trim() !== partner.name) aenderung.name = name.trim()
  if (billingRef.trim() !== partner.billingRef) aenderung.billingRef = billingRef.trim()
  if ((stackHost.trim() || null) !== partner.stackHost)
    aenderung.stackHost = stackHost.trim() || null
  if ((stackEndpoint.trim() || null) !== partner.stackEndpoint)
    aenderung.stackEndpoint = stackEndpoint.trim() || null
  if ((baseDomain.trim() || null) !== partner.baseDomain)
    aenderung.baseDomain = baseDomain.trim() || null
  if (whiteLabel !== partner.whiteLabel) aenderung.whiteLabel = whiteLabel
  if (autoApprove !== partner.autoApprove) aenderung.autoApprove = autoApprove
  if ((notizen.trim() || null) !== partner.notizen) aenderung.notizen = notizen.trim() || null
  if ((freigegebeneVersion.trim() || null) !== partner.freigegebeneVersion)
    aenderung.freigegebeneVersion = freigegebeneVersion.trim() || null
  const geaendert = Object.keys(aenderung).length > 0

  async function speichern(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    if (!geaendert) return
    setLaeuft(true)
    setMeldung(null)
    try {
      await partnerAendern(partner.id, aenderung)
      await onGeaendert()
      setMeldung({ art: 'ok', text: 'Gespeichert.' })
    } catch (fehler: unknown) {
      setMeldung({
        art: 'fehler',
        text: fehler instanceof ApiError ? fehler.message : 'Nicht gespeichert.',
      })
    } finally {
      setLaeuft(false)
    }
  }

  async function zustandSetzen(zustand: VerwaltungPartner['zustand']) {
    setZustandLaeuft(true)
    setMeldung(null)
    try {
      await partnerAendern(partner.id, { zustand })
      await onGeaendert()
    } catch (fehler: unknown) {
      setMeldung({
        art: 'fehler',
        text: fehler instanceof ApiError ? fehler.message : 'Nicht gespeichert.',
      })
    } finally {
      setZustandLaeuft(false)
    }
  }

  async function neuerKopplungscode() {
    setServerLaeuft(true)
    setMeldung(null)
    try {
      setKopplungscode(await kopplungscodeErzeugen(partner.id))
      await onGeaendert()
    } catch (fehler: unknown) {
      setMeldung({
        art: 'fehler',
        text: fehler instanceof ApiError ? fehler.message : 'Kein Code erzeugt.',
      })
    } finally {
      setServerLaeuft(false)
    }
  }

  async function serverWeg() {
    setServerLaeuft(true)
    setMeldung(null)
    try {
      await serverAbmelden(partner.id)
      await onGeaendert()
    } catch (fehler: unknown) {
      setMeldung({
        art: 'fehler',
        text: fehler instanceof ApiError ? fehler.message : 'Nicht abgemeldet.',
      })
    } finally {
      setServerLaeuft(false)
    }
  }

  async function jetztKoppeln() {
    setServerLaeuft(true)
    setMeldung(null)
    try {
      const ergebnis = await serverKoppeln(partner.id)
      await onGeaendert()
      if (ergebnis.fehler) setMeldung({ art: 'fehler', text: ergebnis.fehler })
      else setMeldung({ art: 'ok', text: 'Gekoppelt.' })
    } catch (fehler: unknown) {
      setMeldung({
        art: 'fehler',
        text: fehler instanceof ApiError ? fehler.message : 'Nicht gekoppelt.',
      })
    } finally {
      setServerLaeuft(false)
    }
  }

  async function standAbfragen() {
    setServerLaeuft(true)
    setMeldung(null)
    try {
      const ergebnis = await serverAbfragen(partner.id)
      await onGeaendert()
      if (ergebnis.fehler) setMeldung({ art: 'fehler', text: ergebnis.fehler })
    } catch (fehler: unknown) {
      setMeldung({
        art: 'fehler',
        text: fehler instanceof ApiError ? fehler.message : 'Nicht abgefragt.',
      })
    } finally {
      setServerLaeuft(false)
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <form onSubmit={speichern} className="flex max-w-[36rem] flex-col gap-4">
        <h2 className="text-[13px] font-semibold text-primary">Stammdaten</h2>
        <Input isRequired label="Name" size="md" value={name} onChange={setName} />
        <Input
          isRequired
          label="Abrechnungsbezug"
          hint="Kennung im Rechnungssystem (Autotask, weclapp)."
          size="md"
          value={billingRef}
          onChange={setBillingRef}
        />
        <Toggle
          label="Aufträge automatisch freigeben"
          hint="Bereitstellungen laufen ohne Freigabe durch netable."
          isSelected={autoApprove}
          onChange={setAutoApprove}
        />

        <h2 className="mt-2 text-[13px] font-semibold text-primary">Domain</h2>
        <Input
          label="Subdomain"
          hint={
            partner.kundenDomain && !partner.whiteLabel
              ? `Kundenverwaltung unter ${partner.kundenDomain}, Kunden unter <kunde>.${partner.kundenDomain}`
              : 'Unter der Plattform-Domain: <subdomain>.<plattform> für die Kundenverwaltung, <kunde>.<subdomain>.<plattform> für Kunden.'
          }
          size="md"
          value={subdomain}
          onChange={(w) => setSubdomain(w.toLowerCase())}
          placeholder="z. B. netabletest"
        />
        <h2 className="mt-2 text-[13px] font-semibold text-primary">Stack</h2>
        <Input
          label="Stack-Host"
          hint="Server, auf dem der Stack läuft — nur zur Übersicht."
          size="md"
          value={stackHost}
          onChange={setStackHost}
        />
        <Input
          label="Stack-Adresse"
          hint="Öffentliche Adresse der Kundenverwaltung des Partners — dorthin koppelt und fragt die Verwaltung."
          size="md"
          value={stackEndpoint}
          onChange={setStackEndpoint}
          placeholder="https://partner.example.de"
        />
        <Input
          label="Freigegebene Fassung"
          size="md"
          value={freigegebeneVersion}
          onChange={setFreigegebeneVersion}
          placeholder="z. B. 0.8.0"
        />
        <Toggle
          label="White-Label"
          hint="Der Partner tritt unter eigener Domain auf."
          isSelected={whiteLabel}
          onChange={setWhiteLabel}
        />
        <Input
          label="Basis-Domain"
          hint="Nur bei White-Label — Kunden erhalten Hosts darunter."
          size="md"
          value={baseDomain}
          onChange={setBaseDomain}
          isDisabled={!whiteLabel}
        />
        <Input label="Notizen" size="md" value={notizen} onChange={setNotizen} />

        {meldung ? (
          meldung.art === 'ok' ? (
            <p role="status" className="text-sm text-tertiary">
              {meldung.text}
            </p>
          ) : (
            <Fehler>{meldung.text}</Fehler>
          )
        ) : null}
        <div className="flex justify-end">
          <Knopf type="submit" stark disabled={laeuft || !geaendert}>
            Speichern
          </Knopf>
        </div>
      </form>

      <section className="flex max-w-[36rem] flex-col gap-3">
        <h2 className="text-[13px] font-semibold text-primary">Server</h2>
        {partner.server ? (
          <dl className="grid grid-cols-[9rem_1fr] gap-x-4 gap-y-1.5 text-sm">
            <dt className="text-quaternary">Zustand</dt>
            <dd>
              <Status
                ton={
                  partner.server.zustand === 'ok' || partner.server.zustand === 'gekoppelt'
                    ? 'gut'
                    : partner.server.zustand === 'fehler'
                      ? 'stoerung'
                      : 'ruhig'
                }
              >
                {partner.server.zustand}
              </Status>
            </dd>
            <dt className="text-quaternary">Fingerabdruck</dt>
            <dd className="font-mono text-[12.5px] break-all">{partner.server.fingerabdruck}</dd>
            <dt className="text-quaternary">Gekoppelt</dt>
            <dd>
              {new Date(partner.server.gekoppeltAm).toLocaleString('de-DE')} über{' '}
              {partner.server.gekoppeltVon}
            </dd>
            <dt className="text-quaternary">Fassung</dt>
            <dd className="tabular-nums">
              {partner.server.version}
              {partner.server.build ? ` (${partner.server.build})` : ''}
            </dd>
            <dt className="text-quaternary">Letzte Abfrage</dt>
            <dd>
              {partner.server.letzteMeldung
                ? new Date(partner.server.letzteMeldung).toLocaleString('de-DE')
                : '—'}
            </dd>
            <dt className="text-quaternary">Hinterlegung</dt>
            <dd>
              {partner.hinterlegung
                ? `${new Date(partner.hinterlegung.erhaltenAm).toLocaleString('de-DE')}, Schlüsselversionen ${partner.hinterlegung.schluesselversionen.join(', ')}`
                : 'keine'}
            </dd>
          </dl>
        ) : (
          <p className="text-sm text-tertiary">Kein Server gekoppelt.</p>
        )}
        {kopplungscode ? (
          <div className="rounded-xl border border-achtung bg-achtung-flaeche p-4">
            <p className="text-sm font-medium text-achtung-tief">
              Kopplungscode — nur jetzt sichtbar, gültig bis{' '}
              {new Date(kopplungscode.gueltigBis).toLocaleString('de-DE')}.
            </p>
            <pre className="mt-2 overflow-x-auto rounded-lg bg-primary p-3 text-lg font-semibold tracking-[0.15em] text-primary select-all">
              {kopplungscode.code}
            </pre>
          </div>
        ) : null}
        {partner.kopplungscodeOffenBis ? (
          <p className="text-sm text-tertiary">
            Ein Code ist offen bis {new Date(partner.kopplungscodeOffenBis).toLocaleString('de-DE')}
            .{' '}
            {partner.kopplungsversuch?.zuletzt
              ? `Letzter Versuch ${new Date(partner.kopplungsversuch.zuletzt).toLocaleString('de-DE')}: ${partner.kopplungsversuch.fehler ?? 'gekoppelt'}`
              : 'Die Verwaltung versucht es alle 20 Sekunden, sobald eine Stack-Adresse gespeichert ist.'}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Knopf stark disabled={serverLaeuft} onClick={() => void neuerKopplungscode()}>
            {partner.server && !partner.server.abgemeldetAm ? 'Neuer Code' : 'Server koppeln'}
          </Knopf>
          {partner.kopplungscodeOffenBis ? (
            <Knopf disabled={serverLaeuft} onClick={() => void jetztKoppeln()}>
              Jetzt koppeln
            </Knopf>
          ) : null}
          {partner.server && !partner.server.abgemeldetAm ? (
            <>
              <Knopf disabled={serverLaeuft} onClick={() => void standAbfragen()}>
                Stand abfragen
              </Knopf>
              <Knopf gefahr disabled={serverLaeuft} onClick={() => void serverWeg()}>
                Server abmelden
              </Knopf>
            </>
          ) : null}
        </div>
      </section>

      <section className="flex max-w-[36rem] flex-col gap-3">
        <h2 className="text-[13px] font-semibold text-primary">Zustand</h2>
        <p className="text-sm text-tertiary">
          Gesperrt: kein Abruf, keine Aufträge, aber alles bleibt. Gekündigt: der Partner ist
          abgeschlossen; die Mengen der Vergangenheit bleiben für die Abrechnung.
        </p>
        <div className="flex flex-wrap gap-2">
          {partner.zustand !== 'aktiv' ? (
            <Knopf stark disabled={zustandLaeuft} onClick={() => void zustandSetzen('aktiv')}>
              Wieder aktivieren
            </Knopf>
          ) : (
            <Knopf disabled={zustandLaeuft} onClick={() => void zustandSetzen('gesperrt')}>
              Sperren
            </Knopf>
          )}
          {partner.zustand !== 'gekuendigt' ? (
            <Knopf gefahr disabled={zustandLaeuft} onClick={() => void zustandSetzen('gekuendigt')}>
              Kündigen
            </Knopf>
          ) : null}
        </div>
      </section>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Kleinteile
// ---------------------------------------------------------------------------

function Partnermarke({ p }: { p: VerwaltungPartner }) {
  if (p.zustand === 'aktiv') return <Status ton="gut">aktiv</Status>
  if (p.zustand === 'gesperrt') return <Status ton="ruhig">gesperrt</Status>
  return <Status ton="ruhig">gekündigt</Status>
}

function Wert({
  name,
  children,
  achtung,
}: {
  name: string
  children: ReactNode
  achtung?: boolean
}) {
  return (
    <div>
      <dt className="text-[11.5px] text-quaternary">{name}</dt>
      <dd
        className={cx(
          'mt-0.5 font-display text-2xl leading-[1.15] font-bold tabular-nums',
          achtung ? 'text-achtung-tief' : 'text-primary',
        )}
      >
        {children}
      </dd>
    </div>
  )
}

function Zurueck() {
  return (
    <Link
      to="/"
      className="mt-[18px] ml-6 inline-flex w-fit items-center gap-[7px] text-[13px] text-tertiary hover:text-primary"
    >
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="size-3.5">
        <path
          d="M10 3L5 8l5 5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Alle Partner
    </Link>
  )
}
